import React, { useCallback, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faSync } from "@fortawesome/free-solid-svg-icons";
import {
  paymentService,
  formatCurrency,
  formatShortDate,
  formatApiError,
} from "../../../../../services/api";
import { useAuth } from "../../../../../context/authContext";
import RechargeModal from "../../../../utils/components/RechargeModal/RechargeModal";
import PaycashInstructionsModal from "../../../../utils/components/PaycashInstructionsModal";
import styles from "./recargasPage.module.css";

const STATUS_LABELS = {
  PENDING: "Pendiente",
  APPROVED: "Acreditada",
  EXPIRED: "Expirada",
  CANCELLED: "Cancelada",
  REJECTED: "Rechazada",
  ERROR: "Error",
};

function statusClass(status) {
  const s = String(status || "").toUpperCase();
  if (s === "APPROVED") return styles.statusApproved;
  if (s === "PENDING") return styles.statusPending;
  return styles.statusMuted;
}

function normalizeItem(item) {
  if (!item || typeof item !== "object") return null;
  return {
    ...item,
    transaction_id: item.transaction_id || item.id,
    paycash_reference: item.paycash_reference,
    payment_instructions_url: item.payment_instructions_url,
    status: String(item.status || "").toUpperCase(),
    status_display: item.status_display || item.status,
    amount: item.amount,
    created_at: item.created_at,
    expiration_date: item.expiration_date,
  };
}

export default function Recargas() {
  const { user, isAuthenticated } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showRechargeModal, setShowRechargeModal] = useState(false);
  const [instructionsRecharge, setInstructionsRecharge] = useState(null);

  const loadHistory = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    setError("");
    try {
      const result = await paymentService.getHistory();
      if (result.ok) {
        setItems(
          (result.data || []).map(normalizeItem).filter(Boolean)
        );
      } else {
        setError(
          formatApiError(result.raw) || "No se pudo cargar el historial"
        );
      }
    } catch (err) {
      setError("Error de conexión");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    setLoading(true);
    loadHistory();
  }, [loadHistory]);

  const handleRecharge = async (amount) => {
    const result = await paymentService.createRecharge({
      monto: amount,
      descripcion: "Recarga de saldo",
    });

    if (!result.ok) {
      throw new Error(
        formatApiError(result.data) || "No se pudo crear la recarga"
      );
    }

    setInstructionsRecharge(result.data);
    await loadHistory();
  };

  const openPending = (item) => {
    setInstructionsRecharge({
      transaction_id: item.transaction_id,
      paycash_reference: item.paycash_reference,
      payment_instructions_url: item.payment_instructions_url,
      amount: item.amount,
      expiration_date: item.expiration_date,
      status: item.status,
    });
  };

  const currentBalance =
    user?.balance?.amount ?? user?.balance ?? undefined;

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner} />
        <p>Cargando recargas...</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <button
          type="button"
          className={styles.primaryBtn}
          onClick={() => setShowRechargeModal(true)}
        >
          <FontAwesomeIcon icon={faPlus} />
          Nueva recarga
        </button>
        <button
          type="button"
          className={styles.iconBtn}
          onClick={() => {
            setLoading(true);
            loadHistory();
          }}
          title="Actualizar"
          aria-label="Actualizar historial"
        >
          <FontAwesomeIcon icon={faSync} />
        </button>
      </div>

      {error ? <div className={styles.error}>{error}</div> : null}

      {items.length === 0 && !error ? (
        <div className={styles.emptyState}>
          <span className={styles.emptyIcon} aria-hidden>
            💳
          </span>
          <p className={styles.emptyText}>Aún no tienes recargas</p>
          <p className={styles.emptySubtext}>
            Crea una recarga para generar tu referencia PayCash y pagar en
            puntos físicos o canales digitales.
          </p>
          <button
            type="button"
            className={styles.primaryBtn}
            onClick={() => setShowRechargeModal(true)}
          >
            Recargar saldo
          </button>
        </div>
      ) : (
        <div className={styles.list}>
          {items.map((item) => {
            const key = item.transaction_id || item.reference || item.created_at;
            const isPending = item.status === "PENDING";
            const label =
              item.status_display ||
              STATUS_LABELS[item.status] ||
              item.status;

            return (
              <div key={key} className={styles.card}>
                <div className={styles.cardHeader}>
                  <span className={styles.amount}>
                    {formatCurrency(item.amount)}
                  </span>
                  <span
                    className={`${styles.statusBadge} ${statusClass(
                      item.status
                    )}`}
                  >
                    {label}
                  </span>
                </div>
                <div className={styles.cardMeta}>
                  {item.paycash_reference ? (
                    <p>
                      <span className={styles.metaLabel}>Referencia:</span>{" "}
                      {item.paycash_reference}
                    </p>
                  ) : null}
                  {item.created_at ? (
                    <p>
                      <span className={styles.metaLabel}>Fecha:</span>{" "}
                      {formatShortDate(item.created_at)}
                    </p>
                  ) : null}
                </div>
                {isPending &&
                (item.payment_instructions_url || item.paycash_reference) ? (
                  <button
                    type="button"
                    className={styles.continueBtn}
                    onClick={() => openPending(item)}
                  >
                    Continuar / Ver instrucciones
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      <RechargeModal
        isOpen={showRechargeModal}
        onClose={() => setShowRechargeModal(false)}
        currentBalance={currentBalance}
        onRecharge={handleRecharge}
      />

      <PaycashInstructionsModal
        isOpen={!!instructionsRecharge}
        onClose={() => {
          setInstructionsRecharge(null);
          loadHistory();
        }}
        recharge={instructionsRecharge}
        onStatusChange={() => loadHistory()}
      />
    </div>
  );
}
